import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Rocket, 
  Calendar, 
  Briefcase,
  BookOpen,
  MessageSquare,
  Mail,
  Headphones,
  ArrowRight,
  Building,
  Award
} from 'lucide-react';

export const ResourcesPage = () => {
  const resourceSections = [
    {
      title: 'Directories',
      description: 'Explore comprehensive databases of Portland\'s tech ecosystem.',
      items: [
        { name: 'Communities', href: '/communities', icon: Users, description: 'Tech groups and meetups' },
        { name: 'Startups', href: '/startups', icon: Rocket, description: 'Local startup directory' },
        { name: 'Events', href: '/events', icon: Calendar, description: 'Tech events calendar' },
        { name: 'Jobs', href: '/jobs', icon: Briefcase, description: 'Career opportunities' },
      ]
    },
    {
      title: 'Spaces & Places',
      description: 'Physical locations supporting Portland\'s tech community.',
      items: [
        { name: 'Coworking Spaces', href: '/coworking', icon: Building, description: 'Shared workspaces' },
        { name: 'Success Stories', href: '/success-stories', icon: Award, description: 'Portland tech exits' },
      ]
    },
    {
      title: 'Knowledge & Content',
      description: 'Learn from Portland\'s tech community.',
      items: [
        { name: 'Topics', href: '/topics', icon: BookOpen, description: 'Industry insights' },
        { name: 'Blog', href: '/blog', icon: BookOpen, description: 'Articles & updates' },
        { name: 'Forums', href: '/forums', icon: MessageSquare, description: 'Community discussions' },
        { name: 'Podcasts & Media', href: '/podcasts', icon: Headphones, description: 'Audio & video content' },
      ]
    },
    {
      title: 'Stay Connected',
      description: 'Keep up with Portland tech.',
      items: [
        { name: 'Newsletter', href: '/newsletter', icon: Mail, description: 'Weekly updates' },
      ]
    },
  ];

  return (
    <div data-testid="resources-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Portal</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Resources Hub</h1>
          <p className="text-muted-foreground max-w-2xl">
            Your comprehensive guide to Portland's technology resources. 
            Find communities, companies, events, and everything you need to 
            connect with Portland's tech community.
          </p>
        </div>
      </section>

      {/* Resource Sections */}
      <section>
        <div className="container-wide py-12 space-y-16">
          {resourceSections.map((section, sectionIndex) => (
            <div key={section.title} className="animate-slide-up" style={{ animationDelay: `${sectionIndex * 100}ms` }}>
              <div className="mb-8">
                <h2 className="text-heading text-2xl mb-2">{section.title}</h2>
                <p className="text-muted-foreground">{section.description}</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {section.items.map((item, index) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="group"
                    data-testid={`resource-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Card className="card-hover h-full">
                      <CardContent className="p-6">
                        <div className="w-10 h-10 rounded-sm bg-muted group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center mb-4 transition-colors">
                          <item.icon className="w-5 h-5" />
                        </div>
                        <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                          {item.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {item.description}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-border bg-muted/30">
        <div className="container-wide py-16">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="card-hover">
              <CardContent className="p-8">
                <h3 className="text-heading text-xl mb-3">Submit a Listing</h3>
                <p className="text-muted-foreground mb-6">
                  Have a community, startup, or event to add? Help grow Portland's 
                  tech directory by submitting your information.
                </p>
                <Link to="/contact">
                  <Button>
                    Submit <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-8">
                <h3 className="text-heading text-xl mb-3">Partner With Us</h3>
                <p className="text-muted-foreground mb-6">
                  Interested in sponsoring or collaborating? We'd love to hear from 
                  organizations supporting Portland's tech ecosystem.
                </p>
                <Link to="/contact">
                  <Button variant="outline">
                    Contact Us <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
